import { User } from '../types';

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

const PHOTO_CACHE_KEY = 'zps_user_photo_cache';
const PROFILE_CACHE_KEY = 'zps_user_profile_cache';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedData<T> {
    data: T;
    timestamp: number;
    cachedUsername: string | null;
}

interface GraphProfile {
    id: string;
    displayName: string;
    mail: string | null;
    userPrincipalName: string;
    givenName: string;
    surname: string;
    jobTitle: string;
    department: string;
    officeLocation: string;
    mobilePhone: string;
    businessPhones: string[];
    preferredLanguage: string;
}

/**
 * Get cached data if still valid and matches current user
 */
function getCachedData<T>(cacheKey: string, username: string | null = null): T | null {
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp, cachedUsername } = JSON.parse(cached) as CachedData<T>;

            // If username is provided, verify it matches
            if (username && cachedUsername && username.toLowerCase() !== cachedUsername.toLowerCase()) {
                console.log('Cache mismatch: user changed');
                return null;
            }

            if (Date.now() - timestamp < CACHE_DURATION_MS) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Cache read error:', e);
    }
    return null;
}

/**
 * Save data to cache
 */
function setCachedData<T>(cacheKey: string, data: T, username: string | null = null) {
    try {
        const cacheEntry: CachedData<T> = {
            data,
            timestamp: Date.now(),
            cachedUsername: username
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (e) {
        console.warn('Cache write error:', e);
    }
}

/**
 * Fetch user profile from Microsoft Graph API
 */
export async function fetchUserProfile(accessToken: string, username: string | null = null): Promise<GraphProfile> {
    const cached = getCachedData<GraphProfile>(PROFILE_CACHE_KEY, username);
    if (cached) {
        return cached;
    }

    try {
        const response = await fetch(`${GRAPH_BASE_URL}/me`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Graph API error: ${response.status}`);
        }

        const profile = await response.json();
        setCachedData(PROFILE_CACHE_KEY, profile, username);
        return profile;
    } catch (error) {
        console.error('Error fetching user profile:', error);
        throw error;
    }
}

/**
 * Fetch user photo from Microsoft Graph API
 */
export async function fetchUserPhoto(accessToken: string, username: string | null = null): Promise<string | null> {
    const cached = getCachedData<string | null>(PHOTO_CACHE_KEY, username);
    if (cached) {
        return cached;
    }

    try {
        const response = await fetch(`${GRAPH_BASE_URL}/me/photo/$value`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            throw new Error(`Graph API error: ${response.status}`);
        }

        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        setCachedData(PHOTO_CACHE_KEY, dataUrl, username);
        return dataUrl;
    } catch (error) {
        console.error('Error fetching user photo:', error);
        return null;
    }
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Fetch complete user profile with photo
 */
export async function fetchCompleteUserProfile(accessToken: string, username: string | null = null): Promise<Partial<User>> {
    const cachedProfile = getCachedData<GraphProfile>(PROFILE_CACHE_KEY, username);
    const cachedPhoto = getCachedData<string | null>(PHOTO_CACHE_KEY, username);

    if (cachedProfile && cachedPhoto !== undefined) {
        return mapProfileToUser(cachedProfile, cachedPhoto);
    }

    try {
        const batchRequest = {
            requests: [
                { id: "profile", method: "GET", url: "/me" },
                { id: "photo", method: "GET", url: "/me/photo/$value" }
            ]
        };

        const response = await fetch(`${GRAPH_BASE_URL}/$batch`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(batchRequest)
        });

        if (!response.ok) {
            throw new Error(`Graph Batch API error: ${response.status}`);
        }

        const batchResponse = await response.json();

        // Process Responses
        const profileRes = batchResponse.responses.find((r: any) => r.id === 'profile');
        const photoRes = batchResponse.responses.find((r: any) => r.id === 'photo');

        if (!profileRes || profileRes.status !== 200) {
            throw new Error('Failed to fetch user profile from batch');
        }

        const profile = profileRes.body;
        let photoUrl: string | null = null;

        if (photoRes && photoRes.status === 200 && photoRes.body) {
            photoUrl = `data:${photoRes.headers['Content-Type'] || 'image/jpeg'};base64,${photoRes.body}`;
        }

        setCachedData(PROFILE_CACHE_KEY, profile, username);
        setCachedData(PHOTO_CACHE_KEY, photoUrl, username);

        return mapProfileToUser(profile, photoUrl);

    } catch (e) {
        console.error('Batch fetch failed, falling back to sequential', e);
        // Fallback
        const [profile, photoUrl] = await Promise.all([
            fetchUserProfile(accessToken, username),
            fetchUserPhoto(accessToken, username).catch(() => null)
        ]);
        return mapProfileToUser(profile, photoUrl);
    }
}

function mapProfileToUser(profile: GraphProfile, photoUrl: string | null): Partial<User> {
    return {
        id: profile.id, // This is Graph ID, usually same as Azure object ID
        displayName: profile.displayName,
        email: profile.mail || profile.userPrincipalName,
        jobTitle: profile.jobTitle,
        department: profile.department,
        officeLocation: profile.officeLocation,
        photoUrl: photoUrl || undefined
    };
}

export function clearUserProfileCache() {
    localStorage.removeItem(PHOTO_CACHE_KEY);
    localStorage.removeItem(PROFILE_CACHE_KEY);
}

export function getInitials(displayName: string | undefined): string {
    if (!displayName) return 'U';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
}
